/**
 * Browser Stream Viewer Component
 * Displays live Puppeteer browser screencast in chat with adaptive quality
 * 
 * Copyright 2025 Mia srl
 */

import React, { useEffect, useRef, useState } from 'react';
import config from '../config';
import './BrowserStreamViewer.css';

/**
 * Adaptive Stream Manager
 * Detects network quality and requests appropriate streaming quality
 */
class AdaptiveStreamManager {
  constructor() {
    this.networkQuality = 'medium'; // high/medium/low
    this.lastLatencies = [];
    this.maxLatencySamples = 10;
  }

  detectNetworkQuality() {
    // Use Network Information API if available
    if ('connection' in navigator) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      
      if (connection && connection.effectiveType) {
        // Map effective types to quality levels
        const typeMap = {
          'slow-2g': 'low',
          '2g': 'low',
          '3g': 'medium',
          '4g': 'high'
        };
        
        this.networkQuality = typeMap[connection.effectiveType] || 'medium';
        return this.networkQuality;
      }
      
      // Fallback to downlink speed if available
      if (connection && connection.downlink) {
        if (connection.downlink < 1) {
          this.networkQuality = 'low';
        } else if (connection.downlink < 5) {
          this.networkQuality = 'medium';
        } else {
          this.networkQuality = 'high';
        }
        return this.networkQuality;
      }
    }

    // Fallback: Use latency-based detection
    if (this.lastLatencies.length >= 3) {
      const avgLatency = this.lastLatencies.reduce((a, b) => a + b, 0) / this.lastLatencies.length;
      
      if (avgLatency > 500) {
        this.networkQuality = 'low';
      } else if (avgLatency > 200) {
        this.networkQuality = 'medium';
      } else {
        this.networkQuality = 'high';
      }
    }

    return this.networkQuality;
  }

  recordLatency(latency) {
    this.lastLatencies.push(latency);
    if (this.lastLatencies.length > this.maxLatencySamples) {
      this.lastLatencies.shift();
    }
  }

  getQuality() {
    return this.networkQuality;
  }
}

/**
 * BrowserStreamViewer Component
 * 
 * Props:
 * - sessionId: Browser session ID from backend
 * - initialQuality: Initial stream quality (high/medium/low)
 * - onClose: Callback when user closes the viewer
 */
const BrowserStreamViewer = ({ sessionId, initialQuality = 'medium', onClose }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [currentQuality, setCurrentQuality] = useState(initialQuality);
  const [frameCount, setFrameCount] = useState(0);
  const [lastFrameTime, setLastFrameTime] = useState(Date.now());
  const [fps, setFps] = useState(0);
  const [error, setError] = useState(null);
  const [popupWindow, setPopupWindow] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  
  const wsRef = useRef(null);
  const imgRef = useRef(null);
  const streamManagerRef = useRef(new AdaptiveStreamManager());
  const frameTimeoutRef = useRef(null);
  const fpsIntervalRef = useRef(null);

  useEffect(() => {
    connectWebSocket();

    // Calculate FPS every second
    fpsIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const timeDiff = (now - lastFrameTime) / 1000;
      if (timeDiff > 0) {
        setFps(Math.round(frameCount / timeDiff));
      }
    }, 1000);

    return () => {
      disconnectWebSocket();
      if (fpsIntervalRef.current) {
        clearInterval(fpsIntervalRef.current);
      }
      if (frameTimeoutRef.current) {
        clearTimeout(frameTimeoutRef.current);
      }
    };
  }, [sessionId]);

  const connectWebSocket = () => {
    try {
      // Convert HTTP URL to WebSocket URL
      let wsBaseUrl = config.bffUrl
        .replace('http://', 'ws://')
        .replace('https://', 'wss://');
      
      // Remove /api prefix if present (WebSocket routes are at root level)
      wsBaseUrl = wsBaseUrl.replace(/\/api$/, '');

      // Detect initial quality
      const detectedQuality = streamManagerRef.current.detectNetworkQuality();
      const quality = detectedQuality || currentQuality;
      
      const url = `${wsBaseUrl}/ws/browser/${sessionId}?quality=${quality}`;
      
      console.log(`🌐 Connecting to browser stream: ${url}`);

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('✅ Browser stream connected');
        setIsConnected(true);
        setError(null);
        setCurrentQuality(quality);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'frame') {
            // Update image with new frame
            if (imgRef.current && message.data) {
              const frameReceiveTime = Date.now();
              const latency = frameReceiveTime - message.timestamp;
              
              // Record latency for adaptive quality
              streamManagerRef.current.recordLatency(latency);
              
              imgRef.current.src = `data:image/jpeg;base64,${message.data}`;
              setFrameCount(prev => prev + 1);
              
              // Reset frame timeout
              if (frameTimeoutRef.current) {
                clearTimeout(frameTimeoutRef.current);
              }
              
              // Detect stream stall (no frames for 5 seconds)
              frameTimeoutRef.current = setTimeout(() => {
                setError('Stream stalled - no frames received');
              }, 5000);
            }
          } else if (message.type === 'connected') {
            console.log('Browser stream ready');
          }
        } catch (err) {
          console.error('Error handling WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setError('WebSocket connection error');
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        
        if (event.code !== 1000) {
          setError(`Connection closed: ${event.reason || 'Unknown error'}`);
        }
      };

    } catch (err) {
      console.error('Failed to connect WebSocket:', err);
      setError(`Connection failed: ${err.message}`);
    }
  };

  const disconnectWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'User closed viewer');
      wsRef.current = null;
    }
  };

  const changeQuality = (newQuality) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Send quality change message
      wsRef.current.send(JSON.stringify({
        type: 'changeQuality',
        quality: newQuality
      }));
      
      setCurrentQuality(newQuality);
      console.log(`Quality changed to: ${newQuality}`);
    }
  };

  const handleAutoQuality = () => {
    const detectedQuality = streamManagerRef.current.detectNetworkQuality();
    changeQuality(detectedQuality);
  };

  const openInNewWindow = () => {
    // Close existing popup if open
    if (popupWindow && !popupWindow.closed) {
      popupWindow.focus();
      return;
    }

    // Create new window with similar dimensions to Google Meet
    const width = 1024;
    const height = 768;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const newWindow = window.open(
      `/browser-stream-viewer?sessionId=${sessionId}&quality=${currentQuality}`,
      'BrowserStreamViewer',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,alwaysOnTop=yes`
    );

    if (newWindow) {
      setPopupWindow(newWindow);
      
      // Minimize the embedded viewer to avoid conflicts (only one connection per session)
      // Disconnect WebSocket but keep component in DOM
      setIsMinimized(true);
      disconnectWebSocket();
      
      // Monitor popup window close and restore embedded viewer
      const checkClosed = setInterval(() => {
        if (newWindow.closed) {
          clearInterval(checkClosed);
          setPopupWindow(null);
          // Restore the embedded viewer when popup closes
          setIsMinimized(false);
          // Reconnect after a short delay
          setTimeout(() => {
            connectWebSocket();
          }, 300);
        }
      }, 500);
    }
  };

  // Show minimized state when viewer is in popup
  if (isMinimized) {
    return (
      <div className="browser-stream-viewer minimized">
        <div className="browser-stream-header">
          <div className="stream-info">
            <span className="status-indicator">📺 Viewing in separate window</span>
          </div>
          <div className="stream-controls">
            <button 
              onClick={() => {
                if (popupWindow && !popupWindow.closed) {
                  popupWindow.focus();
                }
              }}
              className="focus-window-btn"
              title="Focus popup window"
            >
              Focus Window
            </button>
            <button 
              onClick={onClose}
              className="close-btn"
              title="Close browser viewer"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="browser-stream-viewer">
      <div className="browser-stream-header">
        <div className="stream-info">
          <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
            {isConnected ? '● Live' : '○ Disconnected'}
          </span>
          <span className="fps-counter">{fps} FPS</span>
          <span className="frame-counter">{frameCount} frames</span>
        </div>
        
        <div className="stream-controls">
          <select 
            value={currentQuality} 
            onChange={(e) => changeQuality(e.target.value)}
            className="quality-selector"
            disabled={!isConnected}
          >
            <option value="high">High Quality</option>
            <option value="medium">Medium Quality</option>
            <option value="low">Low Quality</option>
          </select>
          
          <button 
            onClick={handleAutoQuality}
            className="auto-quality-btn"
            disabled={!isConnected}
            title="Detect and set optimal quality"
          >
            Auto
          </button>
          
          <button 
            onClick={openInNewWindow}
            className="open-window-btn"
            disabled={!isConnected}
            title="Open in new window"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
          </button>
          
          <button 
            onClick={onClose}
            className="close-btn"
            title="Close browser viewer"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="browser-stream-content">
        {error && (
          <div className="stream-error">
            <span>⚠️ {error}</span>
            <button onClick={connectWebSocket}>Retry</button>
          </div>
        )}
        
        {!isConnected && !error && (
          <div className="stream-loading">
            <div className="loading-spinner"></div>
            <p>Connecting to browser stream...</p>
          </div>
        )}
        
        <img 
          ref={imgRef}
          className="browser-frame"
          alt="Browser view"
          style={{ display: isConnected ? 'block' : 'none' }}
        />
      </div>
      
      <div className="browser-stream-footer">
        <div className="session-info">
          <span className="session-id" title={sessionId}>
            Session: {sessionId.slice(0, 8)}...
          </span>
          <span className="quality-badge">{currentQuality}</span>
        </div>
      </div>
    </div>
  );
};

export default BrowserStreamViewer;
